import { Icon, Typography } from "@/components/atoms";
import {
  getCardBrandLogo,
  getCardExpiry,
  getCardLastFour,
  getCardNicknameDisplay,
  isCardExpired,
  isCreditCard,
} from "@/components/organisms/PaymentSettings/helpers";
import { ClientPaymentMethod } from "@/redux/apis/clientPayment/types";
import { clsx } from "@/utils";

interface PaymentMethodRowProps {
  method: ClientPaymentMethod;
  /** Show the "(Default)" info-blue badge under the name (ConfirmCardModal per Figma). */
  showDefaultBadge?: boolean;
  /** "boxed" wraps the row in its own bordered surface (modal usage). */
  variant?: "plain" | "boxed";
  className?: string;
}

/**
 * Compact display row for the selected payment method — brand logo tile +
 * nickname + masked last4/expiry (card), or "Company Invoicing" (invoice).
 * Shared by the Booking Summary's Payment card and the ConfirmCardModal.
 */
export const PaymentMethodRow = ({ method, showDefaultBadge = false, variant = "plain", className }: PaymentMethodRowProps) => {
  const isCard = isCreditCard(method);
  // Mirrors Payment Settings' CardRow badge so the summary, confirm modal and confirmation agree with it.
  const expired = isCard && isCardExpired(method.card_data);

  return (
    <div
      className={clsx(
        "flex items-center justify-between gap-3",
        variant === "boxed" && "rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-2.5",
        className
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-neutral-mid-gray bg-white">
          {isCard ? getCardBrandLogo(method.card_data?.issuer) : <Icon name="EmptyWallet" size="sm" className="text-neutral-dark-gray" />}
        </span>
        <div className="min-w-0">
          <Typography variant="small" className="block truncate">
            {isCard ? getCardNicknameDisplay(method) : "Company Invoicing"}
          </Typography>
          {showDefaultBadge && method.default && (
            <Typography variant="small-regular" className="block text-info">
              (Default)
            </Typography>
          )}
          {expired && (
            <Typography variant="small-regular" className="block text-danger">
              (Expired)
            </Typography>
          )}
        </div>
      </div>

      {isCard && (
        <div className="flex shrink-0 flex-col items-end">
          <Typography variant="small">••• {getCardLastFour(method.card_data)}</Typography>
          <Typography variant="small-regular" className={expired ? "text-danger" : "text-neutral-dark-gray"}>
            Expires {getCardExpiry(method.card_data)}
          </Typography>
        </div>
      )}
    </div>
  );
};
